import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Theme: light / dark / split.
 *
 * "split" is a side-by-side preview mode used during the redesign refinement
 * pass; it renders the canvas in two halves so designers can spot regressions
 * across themes. Persists in `localStorage` under `kairos.admin.theme`. The
 * inline bootstrap script in `index.html` reads this key BEFORE React mounts
 * to prevent a light-then-dark flash on first paint.
 *
 * We set BOTH `data-theme` (used by `auth.html`-style attribute toggles) and
 * `.dark` class (used by the prototype's split mode and many `.ka-*`
 * selectors) per HANDOFF §5 #7 — the dual-selector form is required.
 */
export type Theme = 'light' | 'dark' | 'split'

const STORAGE_KEY = 'kairos.admin.theme'
const VALID: ReadonlySet<Theme> = new Set(['light', 'dark', 'split'])

interface ThemeContextValue {
  theme: Theme
  setTheme: (next: Theme) => void
  cycleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && VALID.has(stored as Theme)) return stored as Theme
  } catch {
    // localStorage may be blocked (privacy mode) — fall through.
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

interface ThemeProviderProps {
  readonly children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactNode {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme)

  // Sync DOM + localStorage whenever theme changes.
  useEffect(() => {
    applyTheme(theme)
    try {
      window.localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // ignore — privacy mode / quota.
    }
  }, [theme])

  // Live-react to OS-level theme flips when no explicit preference stored.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent): void => {
      try {
        if (window.localStorage.getItem(STORAGE_KEY)) return
      } catch {
        // fall through — apply OS preference on storage failure.
      }
      setThemeState(event.matches ? 'dark' : 'light')
    }
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
  }, [])

  const cycleTheme = useCallback(() => {
    setThemeState((current) =>
      current === 'light' ? 'dark' : current === 'dark' ? 'split' : 'light',
    )
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, cycleTheme }),
    [theme, setTheme, cycleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>')
  }
  return ctx
}
